// MOD-07 — form builder alan modeli ⇄ FieldsJson round-trip + BE-aynası doğrulama.
//
// FieldsJson wire'da bir JSON string'tir: `[{key,type,label,required,options?,maxLength?,order}]`
// (camelCase). Builder `FormFieldModel[]` üzerinde çalışır; `order` dizideki konumdan
// türetilir, client-only `id` serialize edilmez. Doğrulama BE `FormFieldSchema.ValidateForSave`'i
// birebir aynalar → kaydetmeden önce 400'leri (FORM_INVALID_FIELDS / FORM_FILE_FIELD_NOT_ALLOWED)
// önler ve inline uyarı üretir.

import { slugify } from '@shared/components/SlugInput/slugify';
import { FIELD_TYPES, FORM_CONSTRAINTS, fieldRequiresOptions, type FieldType } from '../constants';
import type { FormFieldModel, FormTypeValue } from '../types';

function newFieldId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `f-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Serbest metinden BE key kısıtına uygun bir alan anahtarı üretir (label → key). */
export function slugifyKey(source: string): string {
  return slugify(source).slice(0, FORM_CONSTRAINTS.maxKeyLength);
}

function normalizeType(value: unknown): FieldType {
  return typeof value === 'string' && (FIELD_TYPES as readonly string[]).includes(value)
    ? (value as FieldType)
    : 'text';
}

/** Palet'ten yeni alan. `defaults.label` verilirse key otomatik türetilir (düzenlenebilir). */
export function createField(
  type: FieldType,
  defaults: { label?: string; key?: string } = {},
): FormFieldModel {
  const label = defaults.label ?? '';
  return {
    id: newFieldId(),
    key: defaults.key ?? (label ? slugifyKey(label) : ''),
    type,
    label,
    required: false,
    options: [],
    maxLength: null,
  };
}

interface SerializedField {
  key: string;
  type: FieldType;
  label: string;
  required: boolean;
  order: number;
  options?: string[];
  maxLength?: number;
}

/** Alan listesini depolama için camelCase JSON string'e çevirir (order = dizi konumu). */
export function serializeFields(fields: FormFieldModel[]): string {
  const out: SerializedField[] = fields.map((f, index) => {
    const item: SerializedField = {
      key: f.key.trim(),
      type: f.type,
      label: f.label.trim(),
      required: f.required,
      order: index,
    };
    if (fieldRequiresOptions(f.type)) {
      item.options = f.options.map((o) => o.trim()).filter((o) => o.length > 0);
    }
    if (f.maxLength != null && f.maxLength > 0) {
      item.maxLength = f.maxLength;
    }
    return item;
  });
  return JSON.stringify(out);
}

/** Depolanan (geçerli) şemayı builder modeline çözer; `order`'a göre sıralı döner. */
export function parseFields(fieldsJson: string | null | undefined): FormFieldModel[] {
  if (!fieldsJson || !fieldsJson.trim()) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(fieldsJson);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  const items = raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r))
    .map((r) => ({
      order: typeof r.order === 'number' ? r.order : 0,
      model: {
        id: newFieldId(),
        key: typeof r.key === 'string' ? r.key : '',
        type: normalizeType(r.type),
        label: typeof r.label === 'string' ? r.label : '',
        required: r.required === true,
        options: Array.isArray(r.options) ? r.options.map((o) => String(o)) : [],
        maxLength: typeof r.maxLength === 'number' && r.maxLength > 0 ? r.maxLength : null,
      } satisfies FormFieldModel,
    }));

  items.sort((a, b) => a.order - b.order);
  return items.map((i) => i.model);
}

/** `file` alanı yalnız Career formunda paletten seçilebilir (BE FORM_FILE_FIELD_NOT_ALLOWED). */
export function canUseFileField(formType: FormTypeValue): boolean {
  return formType === 'Career';
}

export interface FieldValidationError {
  /** null = form seviyesi (alan-bağımsız). */
  fieldId: string | null;
  /** i18n suffix (forms:builder.errors.<code>). */
  code: string;
  key?: string;
}

/** BE `FormFieldSchema.ValidateForSave`'in client aynası — kaydetmeden önce çalışır. */
export function validateFields(
  fields: FormFieldModel[],
  formType: FormTypeValue,
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  if (fields.length > FORM_CONSTRAINTS.maxFields) {
    errors.push({ fieldId: null, code: 'tooManyFields' });
  }

  const seen = new Set<string>();
  let fileCount = 0;

  for (const f of fields) {
    const key = f.key.trim();
    if (key.length === 0) {
      errors.push({ fieldId: f.id, code: 'keyRequired' });
    } else if (key.length > FORM_CONSTRAINTS.maxKeyLength) {
      errors.push({ fieldId: f.id, code: 'keyTooLong' });
    } else {
      const lower = key.toLowerCase();
      if (seen.has(lower)) errors.push({ fieldId: f.id, code: 'keyDuplicate', key });
      seen.add(lower);
    }

    const label = f.label.trim();
    if (label.length === 0) {
      errors.push({ fieldId: f.id, code: 'labelRequired' });
    } else if (label.length > FORM_CONSTRAINTS.maxLabelLength) {
      errors.push({ fieldId: f.id, code: 'labelTooLong' });
    }

    if (fieldRequiresOptions(f.type)) {
      const opts = f.options.map((o) => o.trim()).filter((o) => o.length > 0);
      if (opts.length === 0) errors.push({ fieldId: f.id, code: 'optionsRequired' });
      else if (opts.length > FORM_CONSTRAINTS.maxOptionsPerField) {
        errors.push({ fieldId: f.id, code: 'tooManyOptions' });
      }
    }

    if (f.type === 'file') {
      fileCount += 1;
      if (formType !== 'Career') errors.push({ fieldId: f.id, code: 'fileOnlyCareer' });
      else if (fileCount > 1) errors.push({ fieldId: f.id, code: 'fileSingle' });
    }

    if (f.maxLength != null && f.maxLength <= 0) {
      errors.push({ fieldId: f.id, code: 'maxLengthPositive' });
    }
  }

  return errors;
}
